import { getIntl } from 'umi';

import { CoreEnums } from '@/utils/domain/constants';
import { CoreFormula } from '@/utils/core';
import BaseFieldHandler from '../BaseFieldHandler';

const { FormFieldType, FormFieldStringType } = CoreEnums;

export default class NumericFieldHandler extends BaseFieldHandler {
  static supportedTypes = [
    FormFieldStringType.Int,
    FormFieldStringType.Int32,
    FormFieldStringType.Decimal,
    FormFieldType.Int,
    FormFieldType.Int32,
    FormFieldType.Decimal,
  ];

  handle() {
    const config = this.getBaseConfig();
    const deeps = this.getDependencies();

    const { isDetails } = this.fieldState;
    const { type, format, minValue, maxValue, decimalPlaces, regularExpression } = this.baseConfig;
    const isDecimal = type === FormFieldType.Decimal || type === FormFieldStringType.Decimal;

    const numericRules = [
      ...config.rules,
      {
        validator: (o, value) => {
          if (!value) return Promise.resolve();

          // extract numeric portion first before validating
          const valueToValidate = format ? CoreFormula.extractNumber(value) : value;

          if (!valueToValidate && /[a-zA-Z]/.test(value)) {
            return Promise.reject(
              new Error(getIntl().formatMessage({ id: 'input.validation.numeric' })),
            );
          }

          return Promise.resolve();
        },
        validateTrigger: 'onChange',
      },
      ...(regularExpression
        ? [
            {
              validator: (_, value) => {
                return !value || new RegExp(regularExpression).test(value)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        getIntl().formatMessage(
                          { id: 'input.validation.regexp' },
                          { regexp: regularExpression },
                        ),
                      ),
                    );
              },
            },
          ]
        : []),
    ];

    return {
      ...config,
      // Never use type="number"/"tel": Chrome payment/phone autofill heuristics
      // ignore autocomplete=off for those. Keep text + inputMode for keypads.
      type: 'text',
      inputMode: isDecimal ? 'decimal' : 'numeric',
      autoComplete: 'one-time-code',
      min: Math.max(minValue || 0, 0),
      format,
      isNumeric: true,
      ...(deeps.length > 0 && { deeps }),
      ...(maxValue != null && { max: maxValue }),
      ...(isDecimal && {
        step: `0.${'0'.repeat((decimalPlaces || 2) - 1)}1`,
        stringMode: true,
        isDecimal,
      }),
      rules: numericRules,
    };
  }
}
